/**
 * Modifiers and the stacking rules (Foundry: src/module/actor/modifiers.ts). A modifier carries
 * its predicate and the roll options of the Entry it came from, so it can be re-tested against
 * any statistic's options; adjustments (AdjustModifier) and damage alterations (DamageAlteration)
 * attach to it and apply when the statistic resolves.
 */
import type { DamageAlteration } from "./damage-dice";
import type { RawPredicate } from "./predicate";
import { testPredicate } from "./predicate";
import { sluggify } from "./slug";
import type { Attribute, ModifierType, Rank } from "./values";
import { ATTRIBUTE_LABELS, DAMAGE_TYPE_CATEGORIES, PROFICIENCY_BONUS, RANK_LABELS, isModifierType } from "./values";

export type ModifierKind = "bonus" | "penalty" | "modifier";

/** An AdjustModifier's deferred change to a modifier (Foundry: ModifierAdjustment). */
export interface ModifierAdjustment {
  /** The modifier slug it applies to; null applies to every modifier of the selector. */
  slug: string | null;
  test: (options: Set<string> | string[]) => boolean;
  suppress?: boolean;
  relabel?: string | null;
  /** How many times this adjustment has applied to one statistic. */
  applications?: number;
  getNewValue?: (current: number) => number;
  getDamageType?: (current: string | null) => string | null;
  /** The Entry the adjustment came from, for breakdowns. */
  source?: string | null;
  sourceName?: string | null;
}

export interface Modifier {
  slug: string;
  label: string;
  type: ModifierType;
  /** The numeric value. Foundry calls this `modifier`. */
  value: number;
  /** The value before adjustments applied. */
  original: number;
  ability: Attribute | null;
  predicate: RawPredicate;
  /** Roll options of the Entry that produced this modifier, added to every predicate test. */
  parentOptions: string[];
  /** Whether the modifier counts toward the total after stacking. */
  enabled: boolean;
  /** Whether the predicate excluded it (or an ability modifier lost to a better one). */
  ignored: boolean;
  force: boolean;
  /** The source Entry's UUID, when it came from a Rule Element. */
  source: string | null;
  sourceName: string | null;
  damageType: string | null;
  damageCategory: string | null;
  critical: boolean | null;
  tags: string[];
  hideIfDisabled: boolean;
  kind: ModifierKind;
  domains: string[];
  adjustments: ModifierAdjustment[];
  alterations: DamageAlteration[];
  /** Set by an adjustment that suppressed or changed it, for the breakdown. */
  adjustedBy: string[];
  /** Foundry's FlatModifier `removeAfterRoll`: the Effect ends once a Roll uses this modifier (#19); a predicate is tested with the statistic's options. */
  removeAfterRoll: boolean | "if-enabled" | RawPredicate;
}

export interface ModifierParams {
  slug?: string;
  label: string;
  type?: string;
  value: number;
  ability?: Attribute | null;
  predicate?: RawPredicate;
  parentOptions?: string[];
  enabled?: boolean;
  ignored?: boolean;
  force?: boolean;
  source?: string | null;
  sourceName?: string | null;
  damageType?: string | null;
  damageCategory?: string | null;
  critical?: boolean | null;
  tags?: string[];
  hideIfDisabled?: boolean;
  domains?: string[];
  adjustments?: ModifierAdjustment[];
  alterations?: DamageAlteration[];
  removeAfterRoll?: boolean | "if-enabled" | RawPredicate;
}

export function createModifier(params: ModifierParams): Modifier {
  const type: ModifierType = isModifierType(params.type) ? params.type : "untyped";
  const value = params.value;
  const kind: ModifierKind =
    value >= 0 && type !== "ability" && type !== "untyped" ? "bonus" : value < 0 && type !== "ability" ? "penalty" : "modifier";
  const damageType = params.damageType ?? null;
  const damageCategory = damageType === "bleed" ? "persistent" : (params.damageCategory ?? null);
  const critical = damageCategory === "splash" ? !!params.critical : (params.critical ?? null);
  const label = params.label;
  return {
    slug: sluggify(params.slug ?? label),
    label,
    type,
    value,
    original: value,
    ability: params.ability ?? null,
    predicate: params.predicate ?? [],
    parentOptions: params.parentOptions ?? [],
    enabled: params.enabled ?? true,
    ignored: params.ignored ?? false,
    force: params.force ?? false,
    source: params.source ?? null,
    sourceName: params.sourceName ?? null,
    damageType,
    damageCategory,
    critical,
    tags: [...new Set(params.tags ?? [])],
    hideIfDisabled: params.hideIfDisabled ?? false,
    kind,
    domains: [...(params.domains ?? [])],
    adjustments: [...(params.adjustments ?? [])],
    alterations: [...(params.alterations ?? [])],
    adjustedBy: [],
    removeAfterRoll: params.removeAfterRoll ?? false,
  };
}

/** A copy with the value reset to the original, as Foundry's `Modifier#clone` does. A changed `value` becomes the new original. */
export function cloneModifier(modifier: Modifier, changes: Partial<Modifier> = {}): Modifier {
  const value = changes.value ?? modifier.original;
  return {
    ...modifier,
    predicate: structuredClone(modifier.predicate),
    tags: [...modifier.tags],
    domains: [...modifier.domains],
    adjustments: [...modifier.adjustments],
    alterations: [...modifier.alterations],
    adjustedBy: [],
    ...changes,
    value,
    original: value,
  };
}

/** Set `enabled` and `ignored` from the predicate. A modifier with no predicate is left alone. */
export function testModifier(modifier: Modifier, options: Set<string>): void {
  if (modifier.predicate.length === 0) return;
  const domain = modifier.parentOptions.length > 0 ? new Set([...options, ...modifier.parentOptions]) : options;
  modifier.enabled = testPredicate(modifier.predicate, domain);
  modifier.ignored = !modifier.enabled;
}

/** Foundry's `Modifier#applyAdjustments`: reset to the original value, then apply every adjustment whose test passes. */
export function applyAdjustments(modifier: Modifier, rollOptions: Iterable<string>): void {
  modifier.value = modifier.original;
  modifier.adjustedBy = [];
  if (modifier.adjustments.length === 0) return;
  const allOptions = [...rollOptions, ...modifierRollOptions(modifier)];
  const adjustments = modifier.adjustments.filter((a) => a.test(allOptions));
  if (adjustments.some((a) => a.suppress)) {
    modifier.ignored = true;
    modifier.adjustedBy = adjustments.filter((a) => a.suppress).map((a) => a.sourceName ?? "adjustment");
    return;
  }
  let relabel: string | null = null;
  for (const adjustment of adjustments) {
    const newValue = adjustment.getNewValue?.(modifier.value) ?? modifier.value;
    if (newValue !== modifier.value) {
      modifier.value = newValue;
      relabel = adjustment.relabel ?? null;
      modifier.adjustedBy.push(adjustment.sourceName ?? "adjustment");
    }
  }
  if (relabel) modifier.label = relabel;
  modifier.damageType = adjustments.reduce((damageType: string | null, adjustment) => adjustment.getDamageType?.(damageType) ?? damageType, modifier.damageType);
}

/** Foundry's `adjustModifiers`: reset application counts, then adjust from the largest magnitude down. */
export function adjustModifiers(modifiers: Modifier[], rollOptions: Set<string>): void {
  for (const modifier of modifiers) for (const adjustment of modifier.adjustments) adjustment.applications = 0;
  for (const modifier of [...modifiers].sort((a, b) => Math.abs(b.value) - Math.abs(a.value))) applyAdjustments(modifier, rollOptions);
}

const HIGHER_BONUS = (a: Modifier, b: Modifier): boolean => a.value >= b.value;
const LOWER_PENALTY = (a: Modifier, b: Modifier): boolean => a.value <= b.value;

function applyStacking(best: Record<string, Modifier>, modifier: Modifier, isBetter: (a: Modifier, b: Modifier) => boolean): number {
  const existing = best[modifier.type];
  if (existing === undefined) {
    modifier.enabled = true;
    best[modifier.type] = modifier;
    return modifier.value;
  }
  if (isBetter(modifier, existing)) {
    existing.enabled = false;
    modifier.enabled = true;
    best[modifier.type] = modifier;
    return modifier.value - existing.value;
  }
  modifier.enabled = false;
  return 0;
}

/**
 * Apply the stacking rules in place: the highest bonus and the lowest penalty of each type
 * count, untyped modifiers always stack, and only one ability modifier applies. Returns the total.
 */
export function applyStackingRules(modifiers: Modifier[]): number {
  let total = 0;
  const highestBonus: Record<string, Modifier> = {};
  const lowestPenalty: Record<string, Modifier> = {};

  const abilityModifiers = modifiers.filter((m) => m.type === "ability" && !m.ignored);
  const bestAbility = abilityModifiers.reduce((best: Modifier | null, modifier) => {
    if (best === null) return modifier;
    return modifier.force ? modifier : best.force ? best : modifier.value > best.value ? modifier : best;
  }, null);
  for (const modifier of abilityModifiers) modifier.ignored = modifier !== bestAbility;

  for (const modifier of modifiers) {
    if (modifier.ignored) {
      modifier.enabled = false;
      continue;
    }
    if (modifier.type === "untyped") {
      modifier.enabled = true;
      total += modifier.value;
      continue;
    }
    total += modifier.value < 0 ? applyStacking(lowestPenalty, modifier, LOWER_PENALTY) : applyStacking(highestBonus, modifier, HIGHER_BONUS);
  }
  return total;
}

/** Foundry's StatisticModifier de-duplication: one modifier per slug, preferring enabled then larger. */
export function dedupeModifiers(modifiers: Modifier[]): Modifier[] {
  const seen = new Map<string, Modifier>();
  for (const modifier of modifiers) {
    const existing = seen.get(modifier.slug);
    if (!existing?.enabled || Math.abs(modifier.value) > Math.abs(existing.value)) seen.set(modifier.slug, modifier);
  }
  return [...seen.values()];
}

/**
 * Test, adjust, de-duplicate and stack a list of modifiers, as `new StatisticModifier(slug,
 * modifiers, rollOptions)` does. Mutates the modifiers' enabled flags and returns the survivors and total.
 */
export function resolveModifiers(modifiers: Modifier[], options?: Set<string>): { modifiers: Modifier[]; total: number } {
  if (options && options.size > 0) {
    for (const m of modifiers) testModifier(m, options);
    adjustModifiers(modifiers, options);
  }
  const unique = dedupeModifiers(modifiers);
  const total = applyStackingRules(unique);
  return { modifiers: unique, total };
}

export function createAttributeModifier(attribute: Attribute, mod: number, domains: string[], max?: number, adjustments: ModifierAdjustment[] = []): Modifier {
  const withBased = domains.includes(`${attribute}-based`) ? domains : [...domains, `${attribute}-based`];
  return createModifier({
    slug: attribute,
    label: ATTRIBUTE_LABELS[attribute],
    value: Math.min(mod, max ?? mod),
    type: "ability",
    ability: attribute,
    domains: withBased,
    adjustments,
  });
}

export interface ProficiencyOptions {
  rank: Rank;
  level: number;
  /** Proficiency Without Level: the Level is never added. */
  withoutLevel?: boolean;
  addLevel?: boolean;
  adjustments?: ModifierAdjustment[];
}

export function createProficiencyModifier({ rank, level, withoutLevel = false, addLevel, adjustments = [] }: ProficiencyOptions): Modifier {
  const add = addLevel ?? rank > 0;
  const bonus = PROFICIENCY_BONUS[rank] + (add && !withoutLevel ? level : 0);
  return createModifier({ slug: "proficiency", label: RANK_LABELS[rank], value: bonus, type: "proficiency", adjustments });
}

/** Roll options describing a modifier, as Foundry's `Modifier#getRollOptions` (used by adjustments and predicates). */
export function modifierRollOptions(modifier: Modifier): string[] {
  const options = [`${modifier.kind}:slug:${modifier.slug}`, `${modifier.kind}:type:${modifier.type}`, `${modifier.kind}:value:${modifier.value}`];
  if (modifier.type === "ability" && modifier.ability) options.push(`modifier:ability:${modifier.ability}`);
  options.push(...modifier.tags.map((t) => `${modifier.kind}:tag:${t}`));
  const damageKinds = [modifier.domains.some((d) => /\bdamage$/.test(d)) ? "damage" : null, modifier.domains.some((d) => /\bhealing$/.test(d)) ? "healing" : null].filter((k): k is string => !!k);
  for (const damageKind of damageKinds) {
    options.push(damageKind, `${modifier.kind}:${damageKind}`);
    if (modifier.damageType) {
      options.push(`${damageKind}:type:${modifier.damageType}`, `${modifier.kind}:${damageKind}:type:${modifier.damageType}`);
      const categoryFromType = DAMAGE_TYPE_CATEGORIES[modifier.damageType] ?? null;
      if (damageKind === "damage" && categoryFromType) options.push(`${damageKind}:category:${categoryFromType}`, `${modifier.kind}:${damageKind}:category:${categoryFromType}`);
    }
    if (modifier.damageCategory) options.push(`${damageKind}:category:${modifier.damageCategory}`, `${modifier.kind}:${damageKind}:category:${modifier.damageCategory}`);
    options.push(...modifier.tags.flatMap((t) => [`${damageKind}:tag:${t}`, `${modifier.kind}:${damageKind}:tag:${t}`]));
  }
  return options;
}

/** Foundry's `Modifier#signedValue`: a zero-valued penalty shows as "-0". */
export function signed(value: number): string {
  return value < 0 ? String(value) : `+${value}`;
}
